import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';

import ApprovedProposals from '../ApprovedProposals';
import BulkActionsBar from '../BulkActionsBar';
import DashboardFilter from '../DashboardFilter';
import DashboardListHeader from '../DashboardListHeader';
import PageHeader from 'components/PageHeader';
import ReviewWidget from 'components/ReviewWidget/ReviewWidget';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { FilterIcon, CloseIcon, AddIcon } from 'components/Icons';
import './DashboardHeader.scss';

/**
 * Dashboard header
 * @param {string} userPlanType - User plan type
 * @param {boolean} canLockUser - Can lock user
 * @param {array} teamList - Team list
 * @param {number} approvedCount - Approved count
 * @param {number} acceptanceRate - Acceptance rate
 * @param {function} onChangeSearch - On change search
 * @param {array} filterItems - Filter items
 * @param {array} reportItems - Report items
 * @param {function} onChangeTeamsFilter - On change teams filter
 * @param {function} onDownloadReportFilter - On download report filter
 * @param {function} onChangeFilter - On change filter
 * @param {function} onChangeTagsFilter - On change tags filter
 * @param {boolean} trash - Trash
 * @param {function} onShowTrash - On show trash
 * @param {function} onChangeDate - On change date
 * @param {array} selectedDate - Selected date
 * @param {function} onClicknNewProposal - On click new proposal
 * @param {boolean} isFetchingTeamMembers - Is fetching team members
 * @param {boolean} isTeamMember - Is team member
 * @param {number} reviewWidget - Review widget
 * @param {number} wixReview - Wix review
 * @param {boolean} showButton - Show button
 * @param {boolean} showFilters - Show filters
 * @param {function} setShowFilters - On change show filters
 * @param {boolean} isLocked - Is locked
 * @param {array} tagsData - Tags data
 * @param {string} tagsType - Tags type
 * @param {function} setTagsType - On change tags type
 * @param {array} sortingList - Sorting list
 * @param {function} onChangeProposalSorting - On change proposal sorting
 * @param {string} selectedSorting - Selected sorting
 * @param {array} approvedProposals - Approved proposals
 * @param {number} tagWrapperWidth - Tag wrapper width
 * @param {function} setTagWrapperWidth - On change tag wrapper width
 * @param {function} setTagsData - On change tags data
 * @param {function} onClickItem - On click item
 * @param {function} proposalsVar - On change proposals
 * @param {function} updateProposalTags - On change proposal tags
 * @param {function} setIgnoreUpdates - On change ignore updates
 * @param {array} tagsFilter - Tags filter
 * @param {boolean} showItemLoader - Show item loader
 * @param {function} updateSignedSeenState - On change signed seen state
 * @param {object} loadingMap - Loading map
 * @param {boolean} bulkLoading - Bulk loading
 * @param {boolean} approvedProposalsLoading - Approved proposals loading
 * @param {boolean} haltAnimation - Halt animation
 * @param {function} setLoadingMap - On change loading map
 * @param {array} selectedProposalIds - Selected proposal IDs
 * @param {boolean} showSelectionCheckboxes - Show selection checkboxes
 * @param {function} onToggleSelectionMode - On toggle selection mode
 * @param {function} onBulkDelete - On bulk delete
 * @param {function} onBulkRestore - On bulk restore
 * @param {function} onBulkApprove - On bulk approve
 * @param {function} onBulkDecline - On bulk decline
 * @param {function} onBulkAddOrRemoveTags - On bulk add or remove tags
 * @param {function} onClearSelection - On clear selection
 * @param {object} bulkTagsPopover - Bulk tags popover
 * @param {function} setBulkTagsPopover - On change bulk tags popover
 * @param {function} onBulkTagsConfirm - On bulk tags confirm
 * @param {function} fetchTags - Fetch tags
 */
const DashboardHeader = ({
  userPlanType,
  canLockUser,
  teamList,
  approvedCount,
  acceptanceRate,
  onChangeSearch,
  filterItems,
  reportItems,
  onChangeTeamsFilter,
  onDownloadReportFilter,
  onChangeFilter,
  onChangeTagsFilter,
  trash,
  onShowTrash,
  onChangeDate,
  selectedDate,
  onClicknNewProposal,
  isFetchingTeamMembers,
  isTeamMember,
  reviewWidget,
  wixReview,
  showButton,
  showFilters,
  setShowFilters,
  isLocked,
  tagsData,
  tagsType,
  setTagsType,
  sortingList,
  onChangeProposalSorting,
  selectedSorting,
  approvedProposals,
  tagWrapperWidth,
  setTagWrapperWidth,
  setTagsData,
  onClickItem,
  proposalsVar,
  updateProposalTags,
  setIgnoreUpdates,
  tagsFilter,
  showItemLoader,
  updateSignedSeenState,
  loadingMap,
  bulkLoading,
  approvedProposalsLoading,
  haltAnimation,
  setLoadingMap,
  selectedProposalIds,
  showSelectionCheckboxes,
  onToggleSelectionMode,
  onBulkDelete,
  onBulkRestore,
  onBulkApprove,
  onBulkDecline,
  onBulkAddOrRemoveTags,
  onClearSelection,
  bulkTagsPopover,
  setBulkTagsPopover,
  onBulkTagsConfirm,
  fetchTags,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 992;

  /**
   * Render dashboard header
   * @returns
   */
  return (
    <div className="dashboard-header shrink">
      <ReviewWidget reviewWidget={reviewWidget} wixReview={wixReview} />
      <PageHeader
        pageTitle={
          trash ? (
            'Trash'
          ) : (
            <>
              <span className="above-md">Your</span> Proposals
            </>
          )
        }
        showButton={
          trash ? null : (
            <>
              {showButton && isMobile && (
                <>
                  <Button className="filter-btn" onClick={() => setShowFilters(!showFilters)}>
                    {showFilters ? (
                      <CloseIcon className="close-icon" />
                    ) : (
                      <FilterIcon className="filter-icon" />
                    )}
                  </Button>
                </>
              )}
              <Button
                className="large-btn secondary-btn icon-right"
                icon={isMobile ? null : <AddIcon />}
                onClick={onClicknNewProposal}
                disabled={isLocked}>
                {isMobile ? 'CREATE' : 'NEW PROPOSAL'}
              </Button>
            </>
          )
        }
      />
      <DashboardFilter
        userPlanType={userPlanType}
        canLockUser={canLockUser}
        isTeamMember={isTeamMember}
        teamList={teamList}
        isFetchingTeamMembers={isFetchingTeamMembers}
        approvedCount={approvedCount}
        trash={trash}
        acceptanceRate={acceptanceRate}
        onChangeSearch={onChangeSearch}
        filterItems={filterItems}
        reportItems={reportItems}
        onChangeFilter={onChangeFilter}
        onChangeTagsFilter={onChangeTagsFilter}
        onShowTrash={onShowTrash}
        onChangeTeamsFilter={onChangeTeamsFilter}
        onDownloadReportFilter={onDownloadReportFilter}
        onChangeDate={onChangeDate}
        selectedDate={selectedDate}
        tagsData={tagsData}
        tagsType={tagsType}
        setTagsType={setTagsType}
        sortingList={sortingList}
        onChangeProposalSorting={onChangeProposalSorting}
        selectedSorting={selectedSorting}
        showSelectionCheckboxes={showSelectionCheckboxes}
        onToggleSelectionMode={onToggleSelectionMode}
      />

      {showSelectionCheckboxes && (
        <BulkActionsBar
          selectedCount={selectedProposalIds?.length || 0}
          trash={trash}
          onApprove={onBulkApprove}
          onDecline={onBulkDecline}
          onRestore={onBulkRestore}
          onAddOrRemoveTags={onBulkAddOrRemoveTags}
          onDelete={onBulkDelete}
          onClearSelection={onClearSelection}
          isBulkActionLoading={bulkLoading}
          bulkTagsPopover={bulkTagsPopover}
          setBulkTagsPopover={setBulkTagsPopover}
          tagsFilter={tagsFilter}
          onBulkTagsConfirm={onBulkTagsConfirm}
          fetchTags={fetchTags}
        />
      )}

      <ApprovedProposals
        approvedProposals={approvedProposals}
        isLoading={approvedProposalsLoading || showItemLoader}
        showEmptyMessage={true}
        tagWrapperWidth={tagWrapperWidth}
        setTagWrapperWidth={setTagWrapperWidth}
        trash={trash}
        onClickItem={onClickItem}
        tagsData={tagsData}
        setTagsData={setTagsData}
        proposalsVar={proposalsVar}
        updateProposalTags={updateProposalTags}
        setIgnoreUpdates={setIgnoreUpdates}
        tagsFilter={tagsFilter}
        canLockUser={canLockUser}
        updateSignedSeenState={updateSignedSeenState}
        loadingMap={loadingMap}
        bulkLoading={bulkLoading}
        haltAnimation={haltAnimation}
        setLoadingMap={setLoadingMap}
      />

      <DashboardListHeader />
    </div>
  );
};

/**
 * Dashboard header default props
 */
DashboardHeader.defaultProps = {
  canLockUser: false,
  selectedDate: null,
  teamList: [],
  onChangeTeamsFilter: () => {},
  isTeamMember: false,
  reviewWidget: 0,
  selectedProposalIds: [],
  showSelectionCheckboxes: false,
  onToggleSelectionMode: () => {},
  onBulkDelete: () => {},
  onBulkRestore: () => {},
  onBulkApprove: () => {},
  onBulkDecline: () => {},
  onBulkAddOrRemoveTags: () => {},
  onClearSelection: () => {},
  bulkTagsPopover: null,
  setBulkTagsPopover: () => {},
  onBulkTagsConfirm: () => {},
  fetchTags: () => {},
};

/**
 * Dashboard header prop types
 */
DashboardHeader.propTypes = {
  userPlanType: PropTypes.string,
  canLockUser: PropTypes.bool,
  approvedCount: PropTypes.number.isRequired,
  acceptanceRate: PropTypes.number.isRequired,
  onChangeSearch: PropTypes.func.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  onChangeDate: PropTypes.func.isRequired,
  onChangeTeamsFilter: PropTypes.func,
  onDownloadReportFilter: PropTypes.func.isRequired,
  selectedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  filterItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reportItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onClicknNewProposal: PropTypes.func.isRequired,
  teamList: PropTypes.arrayOf(PropTypes.shape({})),
  isTeamMember: PropTypes.bool,
  reviewWidget: PropTypes.number,
  onChangeProposalSorting: PropTypes.func.isRequired,
  updateSignedSeenState: PropTypes.func,
  fetchTags: PropTypes.func,
};

export default DashboardHeader;
